// src/App.js
import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import {
  LOGIN_URL,
  setTokenFromURL,
  apiFetch,
  redirectToLogin,
} from './utils/auth.js';
import { LicenseInfo } from '@mui/x-license';
import { AllEnterpriseModule, LicenseManager, ModuleRegistry } from 'ag-grid-enterprise';

// Lazy load route components
const HRTool = lazy(() => import('./HRTool.jsx'));
const InitiateOnBoarding = lazy(() => import('./components/InitiateOnBoarding.jsx'));
const InitiateOffboarding = lazy(() => import('./components/InitiateOffboarding.jsx'));

// Regular imports for components used on every route
import { AuthProvider } from './utils/AuthProvider.jsx';
import PersistLogin from './components/common/PersistLogin';
import Unauthorized from './components/common/Unauthorized';
import ProtectedRoute from './components/common/ProtectedRoute';
import { CustomThemeProvider } from './components/common/ThemeContext.jsx';
import LoadingOverlay from './components/common/LoadingOverlay';
import ErrorBoundary from './components/common/ErrorBoundary';
import VersionBanner from './components/common/VersionBanner';

LicenseManager.setLicenseKey(
  "Using_this_{AG_Grid}_Enterprise_key_{AG-079074}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Altop_Energy_LLC}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Galaxy}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{Galaxy}_need_to_be_licensed___{Galaxy}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{11_March_2026}____[v3]_[01]_MTc3MzE4NzIwMDAwMA==2dffc0c72de44d5837765b8aa995bc3f"
);
ModuleRegistry.registerModules([AllEnterpriseModule]);
LicenseInfo.setLicenseKey(
  '0d35ec1c3e600510479c8d5df1e8fbf8Tz0xMDI1NjksRT0xNzY0MTk0NzA3MDAwLFM9cHJlbWl1bSxMTT1wZXJwZXR1YWwsUFY9aW5pdGlhbCxLVj0y'
);

// Loading fallback for lazy-loaded routes
const RouteLoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
    }}
  >
    <CircularProgress />
  </Box>
);

function App() {
  const [iso, setIso] = useState('');
  const [mode, setMode] = useState('light');
  const [loading, setLoading] = useState(true);

  // Prevent immediate double-redirect loops by using a short-lived sessionStorage flag.
  // This avoids redirect storms when backend/login round-trip is in progress.
  const REDIRECT_GUARD_KEY = 'auth_redirect_in_progress';

  useEffect(() => {
    // Extract token from URL (if login redirected back with ?token=...) and remove it from URL.
    const { token, iso: urlIso, mode: urlMode } = setTokenFromURL();
    if (urlIso) setIso(urlIso);

    // Apply the theme IMMEDIATELY from the launch URL (?mode=) or the last-known cached theme so the app
    // paints in the correct theme with no network wait and no flash, then stop blocking first paint.
    // login/apps/ (drawer app-list + theme reconcile) loads in the BACKGROUND below.
    const cachedTheme = localStorage.getItem('altop_theme');
    if (urlMode) setMode(urlMode);
    else if (cachedTheme) setMode(cachedTheme);
    setLoading(false);

    // Background: refresh preferences without blocking render. (apiFetch now self-bootstraps the token,
    // so a 401 here means a genuinely unauthenticated user — keep the redirect guard for that case.)
    const initializeApp = async () => {
      try {
        const response = await apiFetch('login/apps/');
        if (response && response.status === 401) {
          const guard = sessionStorage.getItem(REDIRECT_GUARD_KEY);
          if (!guard) {
            sessionStorage.setItem(REDIRECT_GUARD_KEY, String(Date.now()));
            redirectToLogin();
            return;
          } else {
            console.warn('Auth guard prevented immediate repeated redirect.');
          }
        }
        if (response && response.ok) {
          const data = await response.json();
          if (data?.defaultTheme) {
            setMode(data.defaultTheme);
            try { localStorage.setItem('altop_theme', data.defaultTheme); } catch (e) { /* ignore */ }
          }
        }
      } catch (err) {
        console.error('initializeApp error', err);
      }
    };

    initializeApp();
    // cleanup: remove guard after some time to allow re-attempts later if needed
    const t = setTimeout(() => sessionStorage.removeItem(REDIRECT_GUARD_KEY), 10000);
    return () => clearTimeout(t);
  }, []);

  if (loading) {
    return <LoadingOverlay loading={loading} setLoading={setLoading} />;
  }

  return (
    <ErrorBoundary fullPage errorTitle="Application Error" errorMessage="The application encountered an unexpected error.">
      <AuthProvider>
        <CustomThemeProvider mode={mode} setMode={setMode}>
          <Router>
            <Box>
              <VersionBanner appName="HR-Dashboard" />
              <Suspense fallback={<RouteLoadingFallback />}>
                <Routes>
                  <Route element={<PersistLogin />}>
                    <Route path={LOGIN_URL} />
                    <Route element={<ProtectedRoute />}>
                      <Route path="/" element={
                        <ErrorBoundary fullPage errorTitle="HR Dashboard Error" errorMessage="Unable to load HR Dashboard module.">
                          <HRTool />
                        </ErrorBoundary>
                      } />
                      <Route path="/Employee/Onboard" element={
                        <ErrorBoundary fullPage errorTitle="Onboarding Error" errorMessage="Unable to load Onboarding module.">
                          <InitiateOnBoarding />
                        </ErrorBoundary>
                      } />
                      <Route path="/Employee/Offboard" element={
                        <ErrorBoundary fullPage errorTitle="Offboarding Error" errorMessage="Unable to load Offboarding module.">
                          <InitiateOffboarding />
                        </ErrorBoundary>
                      } />
                      <Route path="*" element={
                        <ErrorBoundary fullPage errorTitle="HR Dashboard Error" errorMessage="Unable to load HR Dashboard module.">
                          <HRTool />
                        </ErrorBoundary>
                      } />
                    </Route>
                    <Route path="/unauthorized" element={<Unauthorized />} />
                  </Route>
                </Routes>
              </Suspense>
            </Box>
          </Router>
        </CustomThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
